// Utility functions for managing background images in localStorage

const STORAGE_KEY = 'sadhana_background';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

/**
 * Save background configuration to localStorage
 * @param {string} imageData - Base64 string or preset ID
 * @param {string} type - 'upload', 'preset', or 'default'
 */
export const saveBackgroundToStorage = (imageData, type) => {
  try {
    const bgConfig = {
      type: type,
      data: imageData,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bgConfig));
    return true;
  } catch (error) {
    console.error('Error saving background:', error);
    return false;
  }
};

/**
 * Load background configuration from localStorage
 * @returns {Object|null} Background configuration or null
 */
export const loadBackgroundFromStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return null;
    return JSON.parse(saved);
  } catch (error) {
    console.error('Error loading background:', error);
    return null;
  }
};

/**
 * Clear background from localStorage (reset to default)
 */
export const clearBackgroundFromStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing background:', error);
    return false;
  }
};

/**
 * Validate image file
 * @param {File} file - File object from input
 * @returns {Object} Validation result with success and message
 */
export const validateImageFile = (file) => {
  if (!file) {
    return { valid: false, message: 'No file selected' };
  }

  // Check file type
  if (!file.type.startsWith('image/')) {
    return { valid: false, message: 'Please upload an image file' };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    return { valid: false, message: `File too large (${sizeMB}MB). Maximum size is 5MB` };
  }

  return { valid: true, message: 'Valid image file' };
};

/**
 * Convert file to base64 string
 * @param {File} file - File object
 * @returns {Promise<string>} Base64 string
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

/**
 * Compress and resize image before storing
 * @param {File} file - File object
 * @param {number} maxWidth - Maximum width (default 1080)
 * @param {number} maxHeight - Maximum height (default 1920)
 * @param {number} quality - JPEG quality 0-1 (default 0.8)
 * @returns {Promise<string>} Compressed base64 string
 */
export const compressImage = (file, maxWidth = 1080, maxHeight = 1920, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        // Create canvas and compress
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to JPEG with compression
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        
        // Check final size
        const sizeKB = (compressedBase64.length * 0.75) / 1024; // Approximate size in KB
        console.log(`Image compressed: ${(img.width)}x${(img.height)} -> ${width}x${height}, ~${sizeKB.toFixed(0)}KB`);
        
        resolve(compressedBase64);
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Check image orientation
 * @param {File} file - File object
 * @returns {Promise<Object>} Orientation info
 */
export const checkImageOrientation = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      const isPortrait = img.height > img.width;
      const orientation = isPortrait ? 'portrait' : 'landscape';
      
      URL.revokeObjectURL(url); // Clean up
      
      resolve({
        isPortrait,
        orientation,
        width: img.width,
        height: img.height,
        aspectRatio: (img.width / img.height).toFixed(2)
      });
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    
    img.src = url;
  });
};

/**
 * Check localStorage availability and quota
 * @returns {Object} Storage info
 */
export const checkStorageAvailability = () => {
  try {
    // Test if localStorage is available
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    
    // Estimate storage usage
    let totalSize = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        totalSize += localStorage[key].length + key.length;
      }
    }
    
    const usedKB = (totalSize / 1024).toFixed(2);
    const usedMB = (totalSize / (1024 * 1024)).toFixed(2);
    
    // iOS Safari typically has 5MB limit, other browsers 5-10MB
    const estimatedLimitMB = 5;
    const availableMB = (estimatedLimitMB - usedMB).toFixed(2);
    
    return {
      available: true,
      usedKB,
      usedMB,
      availableMB,
      estimatedLimitMB,
      hasSpace: availableMB > 0.5 // At least 500KB free
    };
  } catch (e) {
    return {
      available: false,
      error: e.message,
      usedKB: 0,
      usedMB: 0,
      availableMB: 0,
      estimatedLimitMB: 0,
      hasSpace: false
    };
  }
};

/**
 * Clear old uploaded backgrounds to free space
 * Keeps presets but removes uploaded images
 */
export const clearUploadedBackgrounds = () => {
  try {
    const currentBg = loadBackgroundFromStorage();
    if (currentBg && currentBg.type === 'upload') {
      // If current background is an upload, clear it
      clearBackgroundFromStorage();
      return true;
    }
    return false;
  } catch (e) {
    console.error('Error clearing uploads:', e);
    return false;
  }
};
export const getBackgroundStyle = (bgConfig) => {
  if (!bgConfig) {
    return 'url("/background.png")'; // Default cockpit
  }

  switch (bgConfig.type) {
    case 'upload':
      return `url("${bgConfig.data}")`;
    case 'preset':
      return `url("/presets/preset${bgConfig.data}.jpg")`;
    case 'default':
    default:
      return 'url("/background.png")';
  }
};
